<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-6"
        @fetch="() => {fetchFuelSupplyTugboats()}"
      >
        <template #start>
          <vs-button
            v-show="isUserCanAccessMenu(permission.name, 'add')"
            class="button shadow-primary font-semibold"
            color="primary"
            @click="startAdd"
          >
            Add New Fuel Supply
          </vs-button>
          <vs-button
            class="button shadow-secondary font-semibold ml-5"
            color="secondary"
            @click="isDownload = true"
            :disabled="isDownload"
          >
            Export
          </vs-button>
        </template>
      </table-top>
      <data-table
        class="mt-6"
        :table-header="header"
        :table-data="fuelSupplyTugboatsData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        :loading="isBusy"
        :frozen-columns="isActionColumnVisible ? 3 : 2"
        :header-height="'4rem'"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row w-min">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <template v-if="row.isEditing">
              <div
                :class="`flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
                @click="isProcessing ? null : saveEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-check-circle.svg')">
              </div>
              <div
                :class="`flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
                @click="isProcessing ? null : cancelEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-close-circle.svg')">
              </div>
            </template>
            <template v-else>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="startEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-edit.svg')">
              </div>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'delete')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="startDelete(row)"
              >
                <img :src="require('@/assets/shipo-icon-delete.svg')">
              </div>
            </template>
          </div>
        </template>
        <template #route_name="{ row }">
          <div
            class="p-2"
            style="min-width: 200px"
            v-if="row.isEditing"
          >
            <select-with-multiple
              v-model="row.editForm.fuel_supply_route_details"
              class="px-2"
              :options="options.routes"
              placeholder="Select Route"
              :disabled="isProcessing"
              :activate-search="true"
              :is-can-add="true"
              :on-add="handleAdd"
              :is-add-one-by-one="true"
              separator="-"
              :allow-duplicate-values="true"
              :danger="!row.editForm.fuel_supply_route_details.length"
              danger-text="* Required"
            />
          </div>
          <div v-else>
            {{ row.route_name || '-' }}
          </div>
        </template>
        <template
          v-for="hp in options.horsePowers"
          #[`fuel_supply_details_hp_id_${hp.value}`]="{ row }"
        >
          <div
            :key="hp.value"
          >
            <div
              v-if="row.isEditing"
              class="relative p-2"
            >
              <vs-input
                type="text"
                :disabled="isProcessing"
                :value="String(row.editForm.fuel_supply_details[row.editForm.fuel_supply_details.findIndex(fsd => fsd.hp_id === hp.value)].fuel_liters)"
                :placeholder="`Enter fuel liters`"
                @input="val => row.editForm.fuel_supply_details[row.editForm.fuel_supply_details.findIndex(fsd => fsd.hp_id === hp.value)].fuel_liters = val === '' ? 0 : Number(val)"
                @keydown="preventNonNumber"
              />
            </div>
            <div
              v-else
              class="flex flex-col items-end w-full"
            >
              <div>
                {{ row.fuel_supply_details.find(item => item.hp_id == hp.value).fuel_liters }}
              </div>
            </div>
          </div>
        </template>
        <template #status="{ row }">
          <div
            :class="`badge-status ${row.status === 'ACKNOWLEDGED' ? 'active' : 'pending'}`"
          >
            {{ row.status }}
          </div>
        </template>
      </data-table>
      <table-bottom
        class="mt-6"
        @fetch="fetchFuelSupplyTugboats()"
      />
    </div>
    <pop-up
      :active.sync="popUp.isShown"
      is-warning
      title="Delete Fuel Supply"
      left-button="Cancel"
      right-button="Yes, Delete"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, isShownRestore: false, tempId: null, tempName: '' }"
      @leftButton="popUp = { isShown: false, isShownRestore: false, tempId: null, tempName: '' }"
      @rightButton="deleteFuelSupplyTugboat()"
    >
      <p class="text-center mt-1">
        Are you sure want to delete fuel supply for "{{ popUp.tempName }}"?
      </p>
      <p class="text-center">
        This action cannot be undone
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUpSuccess"
      @close="resetTableFilter"
      @ok="resetTableFilter"
      title="Success"
    >
      Fuel Supply successfully deleted.
    </pop-up>
    <pop-up
      :active.sync="popUp.isShownRestore"
      is-restore
      is-warning
      title="Restore Fuel Supply"
      left-button="Cancel"
      right-button="Restore"
      :is-busy="isProcessing"
      @close="closeRestoreModal"
      @leftButton="closeRestoreModal"
      @rightButton="restoreFuelSupplyTugboatData"
    >
      <p class="text-center mt-1">
        Fuel Supply {{ popUp.tempName }} already exists but deleted.
      </p>
      <p class="text-center">
        Would you like to restore?
      </p>
    </pop-up>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import PopUp from '@/components/popUp'
import SelectWithMultiple from '@/components/selectWithMultiple'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'

import downloadUtils from '../../../utils/downloadUtils'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    DataTable,
    TableBottom,
    TableTop,
    SelectWithMultiple,
    PopUp
  },
  data: () => ({
    isBusy: false,
    header: [
      { key: 'no', label: 'No', sortable: false, width: '50px' },
      { key: 'route_name', label: 'Route', sortable: true },
      // index 3 is for HP
      { key: 'status', label: 'Status', sortable: true }
    ],
    filters: {
      sort_by: '',
      sort_dir: '',
      is_active: null
    },
    popUp: {
      isShown: false,
      isShownRestore: false,
      tempId: null,
      tempName: ''
    },
    restoreData: {
      id: null,
      route_name: null
    },
    popUpSuccess: false,
    fuelSupplyTugboatsData: [],
    options: {
      horsePowers: [],
      routes: []
    },
    isDownload: false,
    isProcessing: false
  }),
  computed: {
    ...mapGetters({
      fuelSupplyTugboats: 'FuelSupplyTugboat/fuelSupplyTugboats',
      routes:  'FuelSupplyTugboat/routes',
      horsePowers: 'TugManagement/horsePowers',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      fuelSupplyTugboatsFiles: 'FuelSupplyTugboat/fuelSupplyTugboatsFiles'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    }
  },
  watch: {
    fuelSupplyTugboats: {
      handler(val) {
        if (val) {
          this.fuelSupplyTugboatsData = val.map(item => ({
            ...item,
            isEditing: false,
            editForm: null
          }))
        }
      }
    },
    horsePowers: {
      handler(val) {
        if (val) {
          this.options.horsePowers = val.map(item => ({ text: `HP ${item.horse_power}`, value: item.horse_power_id }))
        }
      }
    },
    routes: {
      handler(val) {
        if (val) {
          this.options.routes = val.map(item => ({ text: item.alias, value: item.alias }))
        }
      }
    },
    'options.horsePowers': {
      handler(val) {
        if (val) {
          // add to header starting from index 3
          this.header.splice(3, 0, ...val.map(v => ({ key: `fuel_supply_details_hp_id_${v.value}`, label: `<b>${v.text}</b><br>Fuel Supply (Liter)` })))
        }
      },
      immediate: true
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.header.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.header.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.header.splice(index, 1);
        }
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadFuelSupplyTugboats();
        }
      }
    },
    fuelSupplyTugboatsFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `Fuel-Supply-Tugboats.xlsx`)
          this.$store.dispatch('FuelSupplyTugboat/resetFuelSupplyTugboatsFiles');
        }
      }
    }
  },
  mounted() {
    this.fetchFuelSupplyTugboats()
    this.fetchHorsePowers()
    this.fetchRoutes()
    if (this.$route.query.acknowledgeSuccess === 'true') {
      this.showSuccessNotification(this, 'Fuel supply information has been successfully acknowledged.')
    }
    if (this.$route.query.acknowledgeFailed === 'true') {
      this.showFailedNotification(this, this.$route.query.acknowledgeMessage)
    }
  },
  methods: {
    async fetchFuelSupplyTugboats() {
      try {
        this.isBusy = true
        const apiFilters = {
          sort_dir: this.filters.sort_dir.toUpperCase() || 'asc'.toUpperCase(),
          sort_by: this.filters.sort_by || 'route_name',
          page: this.currentPage || 1,
          limit: this.pageSize || 10
        };

        await this.$store.dispatch('FuelSupplyTugboat/fetchFuelSupplyTugboats', apiFilters)

        this.isAdding = false
        this.isBusy = false
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Fuel Consumption list, please try again')
      }
    },
    async fetchHorsePowers() {
      try {
        await this.$store.dispatch('TugManagement/fetchHorsePowers')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Horse Powers, please try again')
      }
    },
    async fetchRoutes() {
      try {
        await this.$store.dispatch('FuelSupplyTugboat/fetchRoutes')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch routes, please try again')
      }
    },
    async deleteFuelSupplyTugboat() {
      this.isProcessing = true
      try {
        await this.$store.dispatch('FuelSupplyTugboat/deleteFuelSupplyTugboat', this.popUp.tempId)
        this.popUp = { isShown: false, tempId: null }
        this.popUpSuccess = true
        this.isProcessing = false
      } catch (e) {
        this.isProcessing = false
        this.showFailedNotification(this, 'Failed to delete Tug Management , please try again')
      }
    },
    async resetTableFilter() {
      await this.$store.dispatch('Table/resetConfig')
      await this.fetchFuelSupplyTugboats()
      this.popUpSuccess = false
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy;
      this.filters.sort_dir = sortDir;
      this.fetchFuelSupplyTugboats();
    },
    startAdd() {
      // Check if there's already an active edit or add
      if (this.isAdding || this.fuelSupplyTugboatsData.some(row => row.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first.');
        return;
      }

      // Create new row
      const newRow = {
        id: `new-${Date.now()}`, // Temporary ID for new row
        isEditing: true,
        active: true,
        editForm: {
          route_name: { value: '', isValid: true },
          fuel_supply_details: this.options.horsePowers.map(horsePower => ({
            hp_id: horsePower.value,
            fuel_liters: null
          })),
          fuel_supply_route_details: []
        }
      }

      this.isAdding = true;
      this.fuelSupplyTugboatsData.unshift(newRow);
    },
    async saveEdit(row) {
      // route_name is composed of route_name_1 - route_name_2 - route_name_3
      row.editForm.route_name.value = row.editForm.fuel_supply_route_details.map(item => item.value).join(' - ')

      if (this.validateEditForm(row.editForm)) {
        this.showFailedNotification(this, 'Some fields is empty, please fill it first')
        return
      }

      try {
        const formData = {
          ...Object.keys(row.editForm).reduce((item, key) => {
            item[key] = row.editForm[key].value
            return item
          }, {}),
          fuel_supply_details: row.editForm.fuel_supply_details.map(item => ({
            hp_id: item.hp_id,
            fuel_liters: item.fuel_liters
          })),
          fuel_supply_route_details: row.editForm.fuel_supply_route_details.map((item, index) => ({
            location_name: item.value,
            sequence_order: index + 1
          }))

        }
        this.isProcessing = true;
        const isRestoreData = await this.checkRestoreData(formData);
        if(isRestoreData) {
          this.isProcessing = false;
          return
        }
        if (this.isAdding) {
          // remove active when adding
          delete formData.active
          // Handle add
          await this.$store.dispatch('FuelSupplyTugboat/postFuelSupplyTugboat', formData)
          this.showSuccessNotification(this, 'Successfully added new Fuel Consumption')
        } else {
          // Handle edit
          await this.$store.dispatch('FuelSupplyTugboat/putFuelSupplyTugboat', {
            id: row.id,
            form: formData
          })
          this.showSuccessNotification(this, 'Successfully saved changes')
        }

        // Reset state
        this.cancelAllEdits()
        this.fetchFuelSupplyTugboats()
        this.isProcessing = false;
      } catch ({ error }) {
        this.isProcessing = false;
        this.showFailedNotification(this, error?.errors?.[0]?.message ?? (error?.message || 'Failed to save changes, please try again'))
      }
    },
    async checkRestoreData(formData) {
      try {
        this.isProcessing = true
        const { data } = await this.$store.dispatch('FuelSupplyTugboat/getDeletedFuelSupplyTugboatByRouteName', formData.route_name)
        this.isProcessing = false
        if (data) {
          const {
            id,
            route_name
          } = data
          this.restoreData.id = id;
          if(route_name === formData.route_name) {
            this.restoreData.route_name = route_name;
          }
          this.popUp.isShownRestore = true;
          this.restoreData.formData = formData;
          return true;
        }
        return false;
      } catch  (error) {
        this.isProcessing = false
        this.showFailedNotification(this, error?.message || 'Failed to restore data, please try again')
      }
    },
    closeRestoreModal() {
      this.popUp.isShownRestore = false;
      this.restoreData = {
        id: null,
        route_name: null,
        formData: {}
      };
    },
    async restoreFuelSupplyTugboatData() {
      try {
        // Handle edit
        await this.$store.dispatch('FuelSupplyTugboat/putFuelSupplyTugboat', {
          id: this.restoreData.id,
          form: {
            ...this.restoreData.formData,
            // restore deleted tugboat
            is_restore: true
          }
        })
        this.showSuccessNotification(this, 'Successfully saved changes')
        // Reset state
        this.cancelAllEdits()
        this.closeRestoreModal()
        this.fetchFuelSupplyTugboats()
      } catch ({error}) {
        this.showFailedNotification(this, error?.message || 'Failed to save changes, please try again')
      }
    },
    validateEditForm(form) {
      Object.keys(form).forEach(key => {
        if (form[key].hasOwnProperty('isValid')) {
          form[key].isValid = !!form[key].value
        }
      })

      return Object.keys(form).some(key => form[key].hasOwnProperty('isValid')
        ? !form[key].isValid
        : false
      )
    },
    cancelEdit(row) {
      if (this.isAdding) {
        // Remove the new row if we're canceling an add
        const index = this.fuelSupplyTugboatsData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.fuelSupplyTugboatsData.splice(index, 1)
        }
      } else {
        // Just cancel the edit
        const index = this.fuelSupplyTugboatsData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.$set(this.fuelSupplyTugboatsData[index], 'isEditing', false)
          this.$set(this.fuelSupplyTugboatsData[index], 'editForm', null)
        }
      }
      this.isAdding = false
    },
    cancelAllEdits() {
      // Cancel any existing edit
      this.fuelSupplyTugboatsData.forEach(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false)
          this.$set(row, 'editForm', null)
        }
      })
      this.isAdding = false
    },
    startEdit(row) {
      // Check if there's already an active edit or add
      if (this.isAdding || this.fuelSupplyTugboatsData.some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first.');
        return;
      }

      const index = this.fuelSupplyTugboatsData.findIndex(item => item.id === row.id);
      if (index !== -1) {
        this.$set(this.fuelSupplyTugboatsData[index], 'isEditing', true);
        this.$set(this.fuelSupplyTugboatsData[index], 'editForm', {
          route_name: { value: row.name, isValid: true },
          fuel_supply_details: row.fuel_supply_details.map(detail => ({
            hp_id: detail.hp_id,
            fuel_liters: detail.fuel_liters
          })),
          fuel_supply_route_details: row.fuel_supply_route_details.map((detail, index) => ({
            location_name: detail.location_name,
            sequence_order: index,
            value: detail.location_name,
            text: detail.location_name
          }))
        });
      }
    },
    startDelete(row) {
      // Check if there's already an active edit or add
      if (this.isAdding || this.fuelSupplyTugboatsData.some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first.');
        return;
      }
      this.popUp = { isShown: true, tempId: row.id, tempName: row.route_name }
    },
    handleAdd(searchTerm) {
      const newOption = { value: searchTerm, text: searchTerm };
      this.options.routes.push(newOption);
    },
    async downloadFuelSupplyTugboats() {
      this.isBusy = true;
      try {
        const apiFilters = {
          sort_dir: this.filters.sort_dir.toUpperCase() || 'asc'.toUpperCase(),
          sort_by: this.filters.sort_by || 'route_name'
        };
        await this.$store.dispatch('FuelSupplyTugboat/downloadFuelSupplyTugboats', apiFilters, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          responseType: 'blob'
        });
        this.isBusy = false;
        this.isDownload = false;
      } catch (error) {
        this.isBusy = false;
        this.isDownload = false;
        this.showFailedNotification(this, 'Failed to download fuel supplies, please try again');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  background-color: #493493;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.badge-status {
  background-color: #4CAF5033;
  font-weight: 500;
  color: #4CAF50;
  text-align: center;
  padding: 5px 0;
  border-radius: 5px;

  &.pending {
    background-color: #CCCCCC33;
    color: #A4A4A4;
  }
}
</style>
